import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import api from "@/lib/api";
import { resolveBackendFileUrl } from "@/lib/fileUrls";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import {
  Folders, CheckCircle, Warning, Target, UsersFour, FileText, Lifebuoy,
  ArrowRight, CalendarBlank, Lightning, ChartBar, House, ChatCircleText, CaretDown
} from "@phosphor-icons/react";
import { InsightsTab } from "@/pages/InsightsPage";
import { UnenrolledLeadsBanner } from "@/components/UnenrolledLeadsBanner";

// ─── Renewal row + inline status setter ─────────────────────────────
//
// Session 90 — the dashboard renewals widget gained operator-set
// statuses so the user can close out rows without editing the
// underlying client expiry/fees. Three statuses:
//
//   • invoice_sent_external     — Xero invoice issued, awaiting payment
//                                   → row turns ORANGE, stays visible
//   • renewed_awaiting_payment  — renewed in-system, awaiting payment
//                                   → row turns ORANGE, stays visible
//   • renewed_paid              — fully done → BACKEND filters it out
//                                   (this code never sees those rows)
//
// Pending rows keep the original red-when-urgent treatment.
// ─────────────────────────────────────────────────────────────────────

const RENEWAL_STATUS_LABELS = {
  invoice_sent_external:    "Invoice sent (Xero) — awaiting payment",
  renewed_awaiting_payment: "Renewed — awaiting payment",
  renewed_paid:             "Renewed & paid",
};

const RENEWAL_STATUS_PILL_CSS = {
  // Orange = work-in-progress, no longer red, but still tracked
  invoice_sent_external:
    "bg-[#FFF7ED] text-[#9A3412] border-[#FED7AA]",
  renewed_awaiting_payment:
    "bg-[#FFF7ED] text-[#9A3412] border-[#FED7AA]",
};

function RenewalRow({ it, navigate, onChanged }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const status = it.renewal_status;
  const isAwaitingPayment =
    status === "invoice_sent_external" || status === "renewed_awaiting_payment";
  // Color the days-out number RED only for pending+urgent rows. Once
  // the operator has marked the row "awaiting payment", we calm the
  // urgency to orange — the row is being handled.
  const urgent = !status && it.days_until <= 14;
  const daysCss = urgent
    ? "text-[#DC2626]"
    : (isAwaitingPayment ? "text-[#C2410C]" : "text-[#525252]");
  const draftDateCss = it.draft_overdue
    ? (status ? "text-[#C2410C] font-bold" : "text-[#DC2626] font-bold")
    : "text-[#525252]";

  const setStatus = async (newStatus) => {
    setSaving(true);
    setMenuOpen(false);
    try {
      await api.post(`/dashboard/renewals/${it.id}/status`,
                     newStatus ? { status: newStatus } : {});
      toast.success(
        newStatus === "renewed_paid"
          ? `${it.name} marked renewed & paid — removed from list.`
          : newStatus
            ? `${it.name} → ${RENEWAL_STATUS_LABELS[newStatus]}.`
            : `${it.name} → pending.`
      );
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update status");
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr
      className="border-t border-[#F5F5F5] hover:bg-[#FAFAFA]"
      data-testid={`renewal-row-${it.id}`}
    >
      <td
        className="px-4 py-2.5 font-semibold text-[#0A0A0A] cursor-pointer"
        onClick={() => navigate(`/clients/${it.id}`)}
      >
        {it.name}
      </td>
      <td className="px-3 py-2.5 text-[#525252] tabular-nums">{it.renewal_date}</td>
      <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-[#0A0A0A]">
        {it.estimated_amount != null ? `$${it.estimated_amount.toLocaleString()}` : "—"}
      </td>
      <td className={`px-3 py-2.5 tabular-nums ${draftDateCss}`}>
        {it.draft_post_date}{it.draft_overdue ? " (overdue)" : ""}
      </td>
      <td className={`px-3 py-2.5 text-right tabular-nums font-bold ${daysCss}`}>
        {it.days_until}d
      </td>
      <td className="px-3 py-2.5 relative">
        <button
          type="button"
          onClick={() => setMenuOpen(o => !o)}
          disabled={saving}
          data-testid={`renewal-status-btn-${it.id}`}
          className={`inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded border ${
            isAwaitingPayment
              ? RENEWAL_STATUS_PILL_CSS[status]
              : "border-[#E5E5E5] text-[#525252] hover:border-[#0055FF] hover:text-[#0055FF]"
          }`}
          title={isAwaitingPayment ? RENEWAL_STATUS_LABELS[status] : "Set renewal status"}
        >
          <span className="truncate max-w-[180px]">
            {isAwaitingPayment ? RENEWAL_STATUS_LABELS[status] : "Set status"}
          </span>
          <CaretDown size={10} weight="bold" />
        </button>
        {menuOpen && (
          <>
            {/* Backdrop to close the menu when clicking elsewhere */}
            <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
            <div
              className="absolute right-3 top-full mt-1 z-20 bg-white border border-[#E5E5E5] shadow-lg w-[280px] text-[11px]"
              data-testid={`renewal-status-menu-${it.id}`}
            >
              <button
                type="button"
                onClick={() => setStatus("invoice_sent_external")}
                className="block w-full text-left px-3 py-2 hover:bg-[#FFF7ED] border-b border-[#F5F5F5]"
              >
                <div className="font-semibold text-[#9A3412]">Invoice sent (Xero)</div>
                <div className="text-[#525252]">External invoice issued, awaiting payment.</div>
              </button>
              <button
                type="button"
                onClick={() => setStatus("renewed_awaiting_payment")}
                className="block w-full text-left px-3 py-2 hover:bg-[#FFF7ED] border-b border-[#F5F5F5]"
              >
                <div className="font-semibold text-[#9A3412]">Renewed — awaiting payment</div>
                <div className="text-[#525252]">Renewed in-system, awaiting payment.</div>
              </button>
              <button
                type="button"
                onClick={() => setStatus("renewed_paid")}
                className="block w-full text-left px-3 py-2 hover:bg-[#F0FDF4] border-b border-[#F5F5F5]"
              >
                <div className="font-semibold text-[#15803D]">Renewed &amp; paid — done</div>
                <div className="text-[#525252]">Removes this row from the list.</div>
              </button>
              {status && (
                <button
                  type="button"
                  onClick={() => setStatus(null)}
                  className="block w-full text-left px-3 py-2 hover:bg-[#FAFAFA] text-[#525252]"
                >
                  <div className="font-semibold">Clear status (back to pending)</div>
                </button>
              )}
            </div>
          </>
        )}
      </td>
    </tr>
  );
}


function StatCard({ icon, label, value, sub, onClick, testId, accent }) {
  return (
    <Card
      className={`border border-[#E5E5E5] rounded-none px-3 py-2.5 flex flex-col transition-colors ${onClick ? "cursor-pointer hover:border-[#0055FF] hover:shadow-sm" : ""}`}
      onClick={onClick}
      data-testid={testId}
    >
      <div className="flex items-center gap-1.5 mb-1" style={{ color: accent || "#525252" }}>
        {icon}
        <span className="text-[9px] font-bold uppercase tracking-[0.12em] truncate">{label}</span>
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className="text-xl font-black tracking-tighter text-[#0A0A0A] leading-none" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          {value}
        </span>
        {sub && <span className="text-[10px] text-[#A3A3A3] truncate">{sub}</span>}
      </div>
    </Card>
  );
}

function ProjectTypesCard({ types, navigate }) {
  const [activeTab, setActiveTab] = useState("client");
  const tabs = [
    { key: "client", label: "Client", color: "#0055FF" },
    { key: "lead", label: "Lead", color: "#7C3AED" },
    { key: "internal", label: "Internal", color: "#0A0A0A" },
  ];
  const t = types || { client: 0, lead: 0, internal: 0 };
  const total = (t.client || 0) + (t.lead || 0) + (t.internal || 0);
  const active = tabs.find(x => x.key === activeTab);
  return (
    <Card
      className="border border-[#E5E5E5] rounded-none px-3 py-2.5 flex flex-col transition-colors cursor-pointer hover:border-[#0055FF] hover:shadow-sm"
      onClick={() => navigate(`/projects?type=${activeTab}`)}
      data-testid="stat-projects-types"
    >
      <div className="flex items-center gap-1.5 mb-1" style={{ color: active.color }}>
        <Folders size={14} weight="duotone" />
        <span className="text-[9px] font-bold uppercase tracking-[0.12em] truncate">Active Projects</span>
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className="text-xl font-black tracking-tighter text-[#0A0A0A] leading-none" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          {t[activeTab] || 0}
        </span>
        <span className="text-[10px] text-[#A3A3A3]">{total} total</span>
      </div>
      <div className="mt-1.5 flex gap-0.5" onClick={(e) => e.stopPropagation()}>
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={(e) => { e.stopPropagation(); setActiveTab(tab.key); }}
            className={`flex-1 text-[9px] font-bold uppercase tracking-wider py-0.5 transition-colors whitespace-nowrap ${activeTab === tab.key ? "text-white" : "text-[#525252] hover:bg-[#F5F5F5]"}`}
            style={activeTab === tab.key ? { backgroundColor: tab.color } : {}}
            data-testid={`dashboard-project-tab-${tab.key}`}
          >
            {tab.label} ({t[tab.key] || 0})
          </button>
        ))}
      </div>
    </Card>
  );
}

function MiniTable({ title, icon, items, emptyText, onViewAll, testId }) {
  return (
    <div className="bg-white border border-[#E5E5E5]" data-testid={testId}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E5E5]">
        <div className="flex items-center gap-2">
          {icon}
          <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">{title}</h3>
        </div>
        {onViewAll && (
          <button onClick={onViewAll} className="inline-flex items-center gap-1 text-[10px] font-bold text-[#0055FF] hover:text-[#003ECB] uppercase tracking-wider">
            View All <ArrowRight size={10} weight="bold" />
          </button>
        )}
      </div>
      {items.length === 0 ? (
        <p className="px-4 py-6 text-sm text-[#A3A3A3] text-center">{emptyText}</p>
      ) : (
        <div className="divide-y divide-[#F5F5F5]">
          {items.map((item, i) => (
            <div key={i} className="px-4 py-2.5 flex items-center justify-between hover:bg-[#FAFAFA] transition-colors cursor-pointer" onClick={item.onClick}>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-[#0A0A0A] truncate">{item.title}</p>
                {item.sub && <p className="text-[10px] text-[#A3A3A3] truncate mt-0.5">{item.sub}</p>}
              </div>
              {item.badge && <span className={`px-2 py-0.5 text-[10px] font-bold flex-shrink-0 ml-2 ${item.badgeCls || "bg-[#F5F5F5] text-[#525252]"}`}>{item.badge}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function DashboardPage() {
  const { user, hasRole, canSee } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // 2026-07 client-logo repair sprint — hide the hero image (keep the
  // welcome text) rather than show a broken-image icon if this tenant's
  // logo_url points at a blob that no longer exists.
  const [heroLogoBroken, setHeroLogoBroken] = useState(false);
  const activeTab = searchParams.get("tab") === "insights" ? "insights" : "dashboard";
  const setActiveTab = (t) => {
    const next = new URLSearchParams(searchParams);
    if (t === "dashboard") next.delete("tab"); else next.set("tab", t);
    setSearchParams(next, { replace: true });
  };
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentLeads, setRecentLeads] = useState([]);
  const [recentTickets, setRecentTickets] = useState([]);
  const [recentProposals, setRecentProposals] = useState([]);
  const [renewals, setRenewals] = useState(null);
  const isInternal = user?.user_type === "internal";
  const isAdmin = hasRole("platform_admin");
  const isAdminOrDelivery = isAdmin || hasRole("delivery_admin");

  // Module-level visibility — Dashboard widgets follow the same W/R/— matrix
  // as the rest of the app. Admins bypass via canSee's auto-write.
  const seeProjects  = !isInternal || canSee("projects");
  const seeSupport   = !isInternal || canSee("support");
  const seeLeads     = isInternal && canSee("leads");
  const seeSales     = isInternal && canSee("sales");
  const seeCampaigns = isInternal && canSee("campaigns");
  // Dashboard stat tiles are irrelevant for users with no access at all.
  const hasAnyInternal = isInternal && (seeProjects || seeSupport || seeLeads || seeSales || seeCampaigns);

  useEffect(() => {
    const loadAll = async () => {
      try {
        const [statsRes] = await Promise.all([api.get("/dashboard/stats")]);
        setStats(statsRes.data);
        if (isInternal) {
          const [leadsRes, ticketsRes, proposalsRes] = await Promise.all([
            seeLeads    ? api.get("/crm/leads?is_active=true").catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
            seeSupport  ? api.get("/support/tickets").catch(() => ({ data: [] }))          : Promise.resolve({ data: [] }),
            seeSales    ? api.get("/quotes?latest_only=true").catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
          ]);
          setRecentLeads((leadsRes.data || []).slice(0, 5));
          setRecentTickets((Array.isArray(ticketsRes.data) ? ticketsRes.data : ticketsRes.data?.tickets || []).slice(0, 5));
          setRecentProposals((proposalsRes.data || []).slice(0, 5));
          if (isAdmin) {
            const ren = await api.get("/dashboard/renewals?window_days=45").catch(() => ({ data: null }));
            setRenewals(ren.data);
          }
        } else {
          const ticketsRes = await api.get("/support/tickets").catch(() => ({ data: [] }));
          setRecentTickets((Array.isArray(ticketsRes.data) ? ticketsRes.data : ticketsRes.data?.tickets || []).slice(0, 5));
        }
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    loadAll();
  }, [isInternal, isAdmin, seeLeads, seeSupport, seeSales, seeCampaigns]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <div className="w-6 h-6 border-2 border-[#0055FF] border-t-transparent animate-spin" />
      </div>
    );
  }

  const STATUS_COLORS = {
    open: "bg-blue-50 text-blue-700", in_progress: "bg-amber-50 text-amber-700",
    resolved: "bg-emerald-50 text-emerald-700", closed: "bg-[#F5F5F5] text-[#A3A3A3]",
    draft: "bg-[#F5F5F5] text-[#525252]", sent: "bg-blue-50 text-blue-700",
    accepted: "bg-emerald-50 text-emerald-700", rejected: "bg-red-50 text-red-700",
  };
  const LEAD_CAT_COLORS = {
    "Initial Contact": "bg-blue-50 text-blue-700", "Demo Scheduled": "bg-purple-50 text-purple-700",
    "Proposal Sent": "bg-amber-50 text-amber-700", "Negotiation": "bg-orange-50 text-orange-700",
    "Closed Won": "bg-emerald-50 text-emerald-700",
  };

  return (
    <div className="page-enter p-4 sm:p-6 space-y-4" data-testid="dashboard-page">
      {/* Session 68 pt3 — client hero card. Only renders for client
          users with a logo on file. Shows the company logo + welcome
          message so a client signing in immediately sees their brand
          (instead of the generic Dashboard headline). Internal users
          and clients without a logo fall through to the standard
          header below. */}
      {!isInternal && user?.tenant?.logo_url && (
        <Card className="border border-[#E5E5E5] rounded-none p-5 flex items-center gap-5 flex-wrap" data-testid="client-dashboard-hero">
          {!heroLogoBroken && (
            <img
              src={resolveBackendFileUrl(user.tenant.logo_url)}
              alt={`${user.tenant.name || "Your"} logo`}
              className="w-[240px] h-[80px] object-contain bg-white"
              onError={() => setHeroLogoBroken(true)}
              data-testid="client-dashboard-hero-logo"
            />
          )}
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-1">Welcome to Eyvo Pulse</p>
            <h1 className="text-2xl sm:text-3xl font-black tracking-tighter text-[#0A0A0A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Hi {user?.name?.split(" ")[0]}, {user.tenant.short_name || user.tenant.name}
            </h1>
            <p className="text-xs text-[#525252] mt-1">Your organisation's view of projects, tickets, and documents.</p>
          </div>
        </Card>
      )}

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-black tracking-tighter text-[#0A0A0A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Dashboard
          </h1>
          <p className="text-xs text-[#525252] mt-0.5">
            Welcome back, {user?.name?.split(" ")[0]}
          </p>
        </div>

        {/* Session 88 pt8 — quick entry into the Claude Sonnet 4.5 data chat.
            Admin-only (mirrors the /admin/data-chat route gate). Pulse-blue
            pill that sits next to the Dashboard title so Mike can ask
            questions about live data without hunting for a URL. */}
        {isAdmin && (
          <button
            data-testid="dashboard-data-chat-cta"
            onClick={() => navigate("/admin/data-chat")}
            className="group flex items-center gap-2.5 px-3.5 py-2 rounded-full border border-[#0055FF]/20 bg-[#0055FF]/5 hover:bg-[#0055FF] hover:text-white text-[#0055FF] transition-colors"
          >
            <ChatCircleText size={16} weight="fill" />
            <div className="text-left leading-tight">
              <div className="text-[11px] font-bold uppercase tracking-wider">
                Ask the data
              </div>
              <div className="text-[10px] opacity-70 group-hover:opacity-90">
                Claude Sonnet 4.5 · live SQL + SendGrid
              </div>
            </div>
            <ArrowRight size={12} weight="bold" className="opacity-60 group-hover:translate-x-0.5 transition-transform" />
          </button>
        )}
      </div>

      {/* Session 88 pt10 — Un-enrolled sales-lead alert. Self-hides when
          count = 0. Admin-only (component fetches the count via the
          gated /api/admin/marketing/unenrolled-leads/count endpoint). */}
      {isAdmin && <UnenrolledLeadsBanner />}

      {/* Tab switcher — Dashboard (live counters + recent items) vs
          Insights (breakdowns + trends). Only shown to internal users
          who have at least one module, and hidden altogether for clients. */}
      {isInternal && hasAnyInternal && (
        <div className="flex border-b border-[#E5E5E5] -mt-1" data-testid="dashboard-tabs">
          {[
            { k: "dashboard", label: "Dashboard", icon: House },
            { k: "insights",  label: "Insights",  icon: ChartBar },
          ].map(({ k, label, icon: Icon }) => (
            <button
              key={k}
              onClick={() => setActiveTab(k)}
              className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5 border-b-2 transition-colors ${
                activeTab === k ? "border-[#0055FF] text-[#0055FF]" : "border-transparent text-[#525252] hover:text-[#0A0A0A]"
              }`}
              data-testid={`dashboard-tab-${k}`}
            >
              <Icon size={12} weight={activeTab === k ? "fill" : "regular"} /> {label}
            </button>
          ))}
        </div>
      )}

      {/* Insights tab — only the unique breakdowns, no duplicated KPIs */}
      {isInternal && hasAnyInternal && activeTab === "insights" && <InsightsTab />}

      {/* Dashboard tab content below — shown when tab=dashboard OR there are
          no tabs (no-access state or client user). */}
      {activeTab === "dashboard" && (<>

      {/* Top Stats */}
      <div className={`grid gap-2 ${isInternal ? "grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 xl:grid-cols-7" : "grid-cols-2 sm:grid-cols-4"}`}>
        {isInternal ? (
          seeProjects && <ProjectTypesCard types={stats?.project_types} navigate={navigate} />
        ) : (
          <StatCard icon={<Folders size={14} weight="duotone" />} label="Active Projects" value={stats?.active_projects || 0} onClick={() => navigate("/projects")} testId="stat-active-projects" />
        )}
        {seeProjects && (
          <StatCard icon={<CheckCircle size={14} weight="duotone" />} label="Open Tasks" value={stats?.open_tasks || 0} sub={`${stats?.total_tasks || 0} total`} onClick={() => navigate("/projects")} testId="stat-open-tasks" />
        )}
        {seeSupport && (
          <StatCard icon={<Lifebuoy size={14} weight="duotone" />} label="Open Tickets" value={stats?.open_tickets || 0} sub={`${stats?.resolved_tickets || 0} resolved`} onClick={() => navigate("/support")} testId="stat-open-tickets" accent="#7C3AED" />
        )}
        {seeProjects && (
          <StatCard icon={<Warning size={14} weight="duotone" />} label="Pending CRs" value={stats?.pending_change_requests || 0} testId="stat-pending-crs" accent="#D97706" />
        )}
        {seeLeads && stats?.leads && (
          <StatCard icon={<Target size={14} weight="duotone" />} label="Active Leads" value={stats.leads.active_leads || 0} sub={`${stats.leads.closed_won || 0} won`} onClick={() => navigate("/crm/leads")} testId="stat-leads" accent="#0D9488" />
        )}
        {seeSales && stats?.proposals && (
          <StatCard icon={<FileText size={14} weight="duotone" />} label="Proposals" value={(stats.proposals.draft_proposals || 0) + (stats.proposals.sent_proposals || 0)} sub={`${stats.proposals.accepted_proposals || 0} accepted`} onClick={() => navigate("/quotes")} testId="stat-proposals" accent="#1D4ED8" />
        )}
        {seeCampaigns && stats?.campaigns && (
          <StatCard icon={<UsersFour size={14} weight="duotone" />} label="Active Campaigns" value={stats.campaigns.active_campaigns || 0} sub={`${stats.campaigns.total_campaigns || 0} total`} onClick={() => navigate("/marketing")} testId="stat-campaigns" accent="#BE185D" />
        )}
      </div>

      {/* Empty-state for users with no module access at all */}
      {isInternal && !hasAnyInternal && (
        <div className="bg-white border border-[#E5E5E5] px-6 py-10 text-center" data-testid="dashboard-no-access">
          <Warning size={28} className="mx-auto text-[#D97706] mb-3" weight="duotone" />
          <p className="text-sm font-bold text-[#0A0A0A]">You don't have access to any modules yet.</p>
          <p className="text-xs text-[#525252] mt-1">Ask a Platform Admin to grant you access on the Team page.</p>
        </div>
      )}

      {/* Lead Pipeline (internal only) */}
      {isInternal && seeLeads && stats?.leads && (
        <div className="bg-white border border-[#E5E5E5] px-3 py-2" data-testid="lead-pipeline">
          <div className="flex items-center justify-between mb-1.5">
            <h3 className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#A3A3A3]">Sales Pipeline</h3>
            <button onClick={() => navigate("/crm/leads")} className="text-[10px] font-bold text-[#0055FF] hover:text-[#003ECB] uppercase tracking-wider inline-flex items-center gap-1">
              View All <ArrowRight size={10} weight="bold" />
            </button>
          </div>
          <div className="flex gap-2">
            {[
              { label: "Initial Contact", value: stats.leads.initial_contact, color: "bg-blue-500" },
              { label: "Demo Scheduled", value: stats.leads.demo_scheduled, color: "bg-purple-500" },
              { label: "Proposal Sent", value: stats.leads.proposal_sent, color: "bg-amber-500" },
              { label: "Negotiation", value: stats.leads.negotiation, color: "bg-orange-500" },
              { label: "Closed Won", value: stats.leads.closed_won, color: "bg-emerald-500" },
            ].map(stage => (
              <div key={stage.label} className="flex-1 text-center">
                <div className={`h-1 ${stage.color} mb-1`} />
                <p className="text-base font-black text-[#0A0A0A] leading-none">{stage.value || 0}</p>
                <p className="text-[9px] text-[#A3A3A3] font-bold uppercase tracking-wider mt-0.5 truncate">{stage.label}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Renewals (admin only) */}
      {isAdmin && renewals && (
        <div className="bg-white border border-[#E5E5E5]" data-testid="renewals-widget">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E5E5] flex-wrap gap-2">
            <div>
              <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">Upcoming Renewals — next {renewals.window_days} days</h3>
              <p className="text-[11px] text-[#A3A3A3] mt-0.5">
                {renewals.total_count} client{renewals.total_count === 1 ? "" : "s"}
                {renewals.total_annual_fees > 0 && (
                  <span> · <strong className="text-[#0A0A0A]">${renewals.total_annual_fees.toLocaleString()}</strong> in fees</span>
                )}
              </p>
            </div>
            <button onClick={() => navigate("/clients")} className="text-[10px] font-bold text-[#0055FF] hover:text-[#003ECB] uppercase tracking-wider inline-flex items-center gap-1">
              All Clients <ArrowRight size={10} weight="bold" />
            </button>
          </div>
          {renewals.total_count === 0 ? (
            <div className="px-5 py-10 text-center">
              <CalendarBlank size={28} className="mx-auto text-[#A3A3A3] mb-2" />
              <p className="text-sm text-[#525252]">No client renewals due in the next {renewals.window_days} days.</p>
              <p className="text-[11px] text-[#A3A3A3] mt-1">When one comes up you'll see the client, renewal date, estimated amount and draft-invoice posting date here.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[#FAFAFA] text-[10px] uppercase tracking-wider text-[#A3A3A3]">
                <tr>
                  <th className="text-left px-4 py-2 font-bold">Client</th>
                  <th className="text-left px-3 py-2 font-bold">Renewal date</th>
                  <th className="text-right px-3 py-2 font-bold">Est. renewal</th>
                  <th className="text-left px-3 py-2 font-bold">Draft invoice posts</th>
                  <th className="text-right px-3 py-2 font-bold">Days out</th>
                  <th className="text-left px-3 py-2 font-bold">Status</th>
                </tr>
              </thead>
              <tbody>
                {(renewals.items || []).slice(0, 25).map(it => (
                  <RenewalRow
                    key={it.id}
                    it={it}
                    navigate={navigate}
                    onChanged={() => api.get("/dashboard/renewals?window_days=45")
                                       .then(r => setRenewals(r.data))
                                       .catch(() => {})}
                  />
                ))}
                {renewals.items && renewals.items.length > 25 && (
                  <tr className="border-t border-[#F5F5F5]">
                    <td colSpan={6} className="px-4 py-2 text-[10px] text-[#A3A3A3] text-center">
                      +{renewals.items.length - 25} more renewals in window — see Clients page
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Main content grid */}
      <div className={`grid gap-6 ${isInternal ? "grid-cols-1 lg:grid-cols-3" : "grid-cols-1 lg:grid-cols-2"}`}>
        {/* Recent Support Tickets */}
        {seeSupport && (
        <MiniTable
          title="Support Tickets"
          icon={<Lifebuoy size={14} className="text-[#7C3AED]" />}
          testId="recent-tickets"
          emptyText="No support tickets"
          onViewAll={() => navigate("/support")}
          items={recentTickets.map(t => ({
            title: t.subject || t.title || `Ticket #${t.ticket_number}`,
            sub: t.tenant_name,
            badge: t.status?.replace("_", " "),
            badgeCls: STATUS_COLORS[t.status] || "bg-[#F5F5F5] text-[#525252]",
            onClick: () => navigate("/support"),
          }))}
        />
        )}

        {/* Recent Leads (internal) */}
        {isInternal && seeLeads && (
          <MiniTable
            title="Recent Leads"
            icon={<Target size={14} className="text-[#0D9488]" />}
            testId="recent-leads"
            emptyText="No leads yet"
            onViewAll={() => navigate("/crm/leads")}
            items={recentLeads.map(l => ({
              title: l.company_name,
              sub: l.contact_person,
              badge: l.lead_category,
              badgeCls: LEAD_CAT_COLORS[l.lead_category] || "bg-[#F5F5F5] text-[#525252]",
              onClick: () => navigate(`/crm/leads/${l.lead_id}`),
            }))}
          />
        )}

        {/* Recent Proposals (internal) */}
        {isInternal && seeSales && (
          <MiniTable
            title="Recent Proposals"
            icon={<FileText size={14} className="text-[#1D4ED8]" />}
            testId="recent-proposals"
            emptyText="No proposals yet"
            onViewAll={() => navigate("/quotes")}
            items={recentProposals.map(q => ({
              title: `${q.quote_number} — ${q.recipient_name || ""}`,
              sub: `${q.currency_code} ${(q.total_amount || 0).toLocaleString()}`,
              badge: q.status?.replace("_", " "),
              badgeCls: STATUS_COLORS[q.status] || "bg-[#F5F5F5] text-[#525252]",
              onClick: () => navigate(`/quotes/${q.quote_id}`),
            }))}
          />
        )}

        {/* Milestones + Activity for clients */}
        {!isInternal && (
          <MiniTable
            title="Upcoming Milestones"
            icon={<CalendarBlank size={14} className="text-[#0055FF]" />}
            testId="upcoming-milestones"
            emptyText="No upcoming milestones"
            items={(stats?.upcoming_milestones || []).map(m => ({
              title: m.title,
              sub: `${m.project_name} - Due: ${m.due_date}`,
              badge: m.status,
              badgeCls: m.status === "completed" ? "bg-emerald-50 text-emerald-700" : "bg-[#F5F5F5] text-[#525252]",
            }))}
          />
        )}
      </div>

      {/* Activity Feed — Platform Admin / Delivery Admin only, per PM directive */}
      {isInternal && isAdminOrDelivery && stats?.recent_activity?.length > 0 && (
        <div className="bg-white border border-[#E5E5E5]">
          <div className="px-4 py-3 border-b border-[#E5E5E5]">
            <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-[#A3A3A3] flex items-center gap-2">
              <Lightning size={14} className="text-[#0055FF]" /> Recent Activity
            </h3>
          </div>
          <div className="divide-y divide-[#F5F5F5]">
            {stats.recent_activity.slice(0, 6).map((log) => (
              <div key={log.id} className="px-4 py-2.5 flex items-start gap-2 text-xs text-[#525252]">
                <div className="w-1 h-1 rounded-full bg-[#0055FF] mt-1.5 flex-shrink-0" />
                <span className="flex-1">{log.action?.replace(/_/g, " ")} - {log.details?.slice(0, 80)}</span>
                <span className="text-[10px] text-[#A3A3A3] flex-shrink-0">{log.user_name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      </>)}
    </div>
  );
}
